import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  fmtKr,
  fmtKwh,
  fmtTid,
  type Charger,
  type Me,
  type Person,
  type SessionRow,
} from '../lib/api.ts';
import { Badge, Field, inputKlasse, knappPrimær, knappSekundær } from '../components/ui.tsx';

export default function ChargerDetail({ me }: { me: Me }) {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [melding, setMelding] = useState<string | null>(null);

  const chargersQuery = useQuery<Charger[]>({
    queryKey: ['chargers'],
    queryFn: () => api.get('/chargers'),
  });
  const charger = chargersQuery.data?.find((c) => c.id === id);

  const sessionsQuery = useQuery<SessionRow[]>({
    queryKey: ['charger-sessions', id],
    queryFn: () => api.get(`/chargers/${id}/sessions`),
    enabled: !!id,
  });

  const personsQuery = useQuery<Person[]>({
    queryKey: ['persons'],
    queryFn: () => api.get('/persons'),
    enabled: me.rolle === 'admin' || me.rolle === 'sysadmin',
  });

  const kommando = useMutation({
    mutationFn: (hva: 'start' | 'stop') => api.post(`/chargers/${id}/${hva}`),
    onSuccess: () => {
      setMelding('Kommando sendt — status oppdateres om noen sekunder');
      setTimeout(() => qc.invalidateQueries({ queryKey: ['chargers'] }), 6_000);
    },
    onError: (err) => setMelding(err instanceof Error ? err.message : 'Feil'),
  });

  const synk = useMutation({
    mutationFn: () => api.post(`/chargers/${id}/sync`),
    onSuccess: () => {
      setMelding('Økter synkronisert');
      qc.invalidateQueries({ queryKey: ['charger-sessions', id] });
    },
    onError: (err) => setMelding(err instanceof Error ? err.message : 'Synk feilet'),
  });

  const oppdater = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/chargers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chargers'] });
      setMelding('Lagret');
    },
    onError: (err) => setMelding(err instanceof Error ? err.message : 'Feil'),
  });

  if (!charger) {
    return (
      <p className="text-slate-500">
        {chargersQuery.isLoading ? 'Laster …' : 'Laderen finnes ikke.'}{' '}
        <Link to="/ladere" className="text-blue-600 underline">
          Tilbake
        </Link>
      </p>
    );
  }

  const state = charger.sistState;
  const kanStyre = me.rolle === 'admin' || me.rolle === 'sysadmin' || charger.personId === me.id;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{charger.navn}</h1>
          <p className="text-sm text-slate-500">
            {charger.anleggNavn} · {charger.prisomrade} · eier: {charger.eierNavn ?? '–'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge state={state} />
          {kanStyre && state?.online && (
            <>
              {state.modus === 'LADER' ? (
                <button onClick={() => kommando.mutate('stop')} disabled={kommando.isPending} className={knappSekundær}>
                  Stopp lading
                </button>
              ) : state.modus !== 'FRAKOBLET' ? (
                <button onClick={() => kommando.mutate('start')} disabled={kommando.isPending} className={knappPrimær}>
                  Lad nå
                </button>
              ) : null}
            </>
          )}
          {(me.rolle === 'admin' || me.rolle === 'sysadmin') && (
            <button onClick={() => synk.mutate()} disabled={synk.isPending} className={knappSekundær}>
              {synk.isPending ? 'Synker …' : 'Synk økter'}
            </button>
          )}
        </div>
      </div>

      {melding && <p className="text-sm text-slate-600">{melding}</p>}

      {state && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Effekt nå" verdi={state.effektKw !== null ? `${state.effektKw.toFixed(1)} kW` : '–'} />
          <Stat label="Energi i økt" verdi={state.oktKwh != null ? fmtKwh(state.oktKwh) : '–'} />
          <Stat label="Status hentet" verdi={fmtTid(charger.sistStateTs)} />
        </div>
      )}

      {(me.rolle === 'admin' || me.rolle === 'sysadmin') && (
        <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Innstillinger</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Navn">
              <input
                defaultValue={charger.navn}
                className={inputKlasse}
                onBlur={(e) => {
                  if (e.target.value !== charger.navn && e.target.value.trim()) {
                    oppdater.mutate({ navn: e.target.value.trim() });
                  }
                }}
              />
            </Field>
            <Field label="Eier (standard kostnadsmottaker)">
              <select
                value={charger.personId ?? ''}
                className={inputKlasse}
                onChange={(e) => oppdater.mutate({ personId: e.target.value || null })}
              >
                <option value="">– ingen –</option>
                {(personsQuery.data ?? [])
                  .filter((p) => p.aktiv)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.navn}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Aktiv">
              <select
                value={charger.aktiv ? 'ja' : 'nei'}
                className={inputKlasse}
                onChange={(e) => oppdater.mutate({ aktiv: e.target.value === 'ja' })}
              >
                <option value="ja">Ja</option>
                <option value="nei">Nei</option>
              </select>
            </Field>
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Ladeøkter siste 30 dager</h2>
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3">Start</th>
                <th className="px-4 py-3">Slutt</th>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3 text-right">kWh</th>
                <th className="px-4 py-3 text-right">Strøm</th>
                <th className="px-4 py-3 text-right">Nettleie</th>
                <th className="px-4 py-3 text-right">Totalt</th>
              </tr>
            </thead>
            <tbody>
              {(sessionsQuery.data ?? []).map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">{fmtTid(s.startTs)}</td>
                  <td className="px-4 py-2">{s.sluttTs ? fmtTid(s.sluttTs) : 'pågår'}</td>
                  <td className="px-4 py-2">
                    {s.personNavn ?? '–'}
                    {s.manueltFlyttet && <span className="ml-1 text-xs text-amber-600" title="Manuelt flyttet">✱</span>}
                  </td>
                  <td className="px-4 py-2 text-right">{Number(s.kwhTotal).toFixed(1)}</td>
                  <td className="px-4 py-2 text-right">{fmtKr(s.kostStrom ?? null)}</td>
                  <td className="px-4 py-2 text-right">{fmtKr(s.kostNett ?? null)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmtKr(s.kostTotal)}</td>
                </tr>
              ))}
              {!sessionsQuery.data?.length && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                    Ingen økter i perioden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, verdi }: { label: string; verdi: string }) {
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-bold text-slate-800">{verdi}</p>
    </div>
  );
}
